import { useState, useEffect, useRef, useCallback } from 'react'
import { flushSync } from 'react-dom'
import { io } from 'socket.io-client'
import { useTranslation } from 'react-i18next'
import { checkAdminOnline, fetchChatConfig } from '../services/chat'
import EmojiPicker from './EmojiPicker'
import './ChatWidget.css'

const API_URL = import.meta.env.VITE_PARSE_SERVER_URL
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || (API_URL ? new URL(API_URL, window.location.origin).origin : 'http://localhost:1337')

function getOrCreateVisitorId() {
  let id = localStorage.getItem('chat_visitor_id')
  if (!id) {
    id = crypto.randomUUID()
    localStorage.setItem('chat_visitor_id', id)
  }
  return id
}

function getStoredName() {
  return localStorage.getItem('chat_visitor_name') || ''
}

function getStoredEmail() {
  return localStorage.getItem('chat_visitor_email') || ''
}

function isDeleted() {
  return localStorage.getItem('chat_deleted') === '1'
}

function storeIdentity(name, email) {
  localStorage.setItem('chat_visitor_name', name)
  localStorage.setItem('chat_visitor_email', email)
}

export default function ChatWidget() {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [showForm, setShowForm] = useState(isDeleted() || !getStoredName())
  const [name, setName] = useState(getStoredName())
  const [email, setEmail] = useState(getStoredEmail())
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [connected, setConnected] = useState(false)
  const [adminTyping, setAdminTyping] = useState(false)
  const [conversationId, setConversationId] = useState(null)
  const [adminOnline, setAdminOnline] = useState(false)
  const [withinHours, setWithinHours] = useState(true)
  const [closed, setClosed] = useState(false)
  const [cookieVisible, setCookieVisible] = useState(false)
  const socketRef = useRef(null)
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)
  const messagesRef = useRef(null)
  const olderOffsetRef = useRef(0)
  const typingTimeoutRef = useRef(null)
  const connectingRef = useRef(false)
  const pendingStartChat = useRef(false)
  const [hasMoreMessages, setHasMoreMessages] = useState(false)
  const [loadingOlder, setLoadingOlder] = useState(false)

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 50)
  }, [])

  const isNearBottom = () => {
    const el = messagesRef.current
    if (!el) return true
    return el.scrollHeight - el.scrollTop - el.clientHeight < 80
  }

  const handleScroll = () => {
    const el = messagesRef.current
    if (!el || !hasMoreMessages || loadingOlder) return
    if (el.scrollTop < 40) {
      setLoadingOlder(true)
      socketRef.current?.emit('visitor:load-older', { skip: olderOffsetRef.current, limit: 50 })
    }
  }

  useEffect(() => {
    checkAdminOnline().then(data => {
      if (data) setAdminOnline(!!data.online)
    }).catch(() => {})
    fetchChatConfig().then(data => {
      if (data && typeof data.withinHours === 'boolean') setWithinHours(data.withinHours)
    }).catch(() => {})
    setCookieVisible(!localStorage.getItem('analytics_consent'))

    const onStorage = (e) => {
      if (e.key === 'analytics_consent') setCookieVisible(false)
    }
    window.addEventListener('storage', onStorage)
    const interval = setInterval(() => {
      if (cookieVisible && localStorage.getItem('analytics_consent')) setCookieVisible(false)
    }, 2000)
    const adminPoll = setInterval(() => {
      checkAdminOnline().then(data => {
        if (data) setAdminOnline(!!data.online)
      }).catch(() => {})
    }, 30000)
    return () => {
      window.removeEventListener('storage', onStorage)
      clearInterval(interval)
      clearInterval(adminPoll)
    }
  }, [cookieVisible])

  const connectSocket = useCallback(() => {
    if (socketRef.current || connectingRef.current) return
    connectingRef.current = true

    const visitorId = getOrCreateVisitorId()
    const storedConvId = localStorage.getItem('chat_conversation_id') || ''

    const socket = io(`${BACKEND_URL}/chat`, {
      query: { visitorId, visitorName: '', visitorEmail: '', conversationId: storedConvId },
      transports: ['websocket', 'polling'],
    })

    socket.on('connect', () => {
      setConnected(true)
    })

    socket.on('admin:online', ({ count }) => {
      setAdminOnline(count > 0)
    })

    socket.on('disconnect', () => {
      setConnected(false)
      connectingRef.current = false
    })

    socket.on('visitor:no-conversation', () => {
      connectingRef.current = false
      if (pendingStartChat.current) {
        pendingStartChat.current = false
        socket.emit('visitor:start-chat', { name: getStoredName(), email: getStoredEmail() })
      } else {
        setShowForm(true)
      }
    })

    socket.on('visitor:conversation', (conv) => {
      localStorage.setItem('chat_conversation_id', conv.objectId)
      setConversationId(conv.objectId)
      setShowForm(false)
      setClosed(conv.status === 'closed')
      if (pendingStartChat.current && conv.status === 'closed') {
        pendingStartChat.current = false
        socket.emit('visitor:start-chat', { name: getStoredName(), email: getStoredEmail() })
      }
    })

    socket.on('visitor:messages', (data) => {
      const msgs = data?.results || []
      setMessages(msgs)
      olderOffsetRef.current = msgs.length
      setHasMoreMessages(!!data?.hasMore)
      setLoadingOlder(false)
      scrollToBottom()
    })

    socket.on('visitor:older-messages', (data) => {
      const older = data?.results || []
      if (older.length > 0) {
        const el = messagesRef.current
        const prevHeight = el?.scrollHeight || 0
        flushSync(() => {
          setMessages(prev => [...older, ...prev])
        })
        olderOffsetRef.current += older.length
        if (el) el.scrollTop = el.scrollHeight - prevHeight
      }
      setHasMoreMessages(!!data?.hasMore)
      setLoadingOlder(false)
    })

    socket.on('message:new', (msg) => {
      setMessages(prev => {
        if (prev.some(m => m.objectId === msg.objectId)) return prev
        if (msg.senderRole === 'visitor' && prev.some(m => m._temp && m.content === msg.content && m.senderRole === 'visitor')) {
          return prev.map(m => m._temp && m.content === msg.content ? { ...msg, _temp: false } : m)
        }
        return [...prev, msg]
      })
      if (isNearBottom()) scrollToBottom()
    })

    socket.on('typing:admin', ({ typing }) => {
      setAdminTyping(typing)
    })

    socket.on('conversation:closed', () => {
      setClosed(true)
      scrollToBottom()
    })

    socket.on('conversation:deleted', () => {
      localStorage.removeItem('chat_conversation_id')
      localStorage.setItem('chat_deleted', '1')
      setConversationId(null)
      setMessages([])
      setClosed(false)
      setShowForm(true)
      olderOffsetRef.current = 0
      setHasMoreMessages(false)
      setLoadingOlder(false)
    })

    socketRef.current = socket
  }, [scrollToBottom])

  useEffect(() => {
    if (open) {
      connectSocket()
    }
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
    }
  }, [open, showForm, connectSocket])

  useEffect(() => {
    if (open) scrollToBottom()
  }, [open, scrollToBottom])

  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)

  const handleStartChat = (e) => {
    e.preventDefault()
    if (!name.trim() || !isValidEmail(email.trim())) return

    localStorage.removeItem('chat_deleted')
    localStorage.removeItem('chat_conversation_id')
    storeIdentity(name.trim(), email.trim())
    setShowForm(false)
    setClosed(false)
    setMessages([])
    olderOffsetRef.current = 0
    setHasMoreMessages(false)
    setLoadingOlder(false)

    pendingStartChat.current = true
    if (socketRef.current) {
      socketRef.current.disconnect()
      socketRef.current = null
      connectingRef.current = false
    }
    connectSocket()
  }

  const handleNewChat = () => {
    localStorage.removeItem('chat_deleted')
    localStorage.removeItem('chat_conversation_id')
    setClosed(false)
    setConversationId(null)
    setMessages([])
    setShowForm(true)
    olderOffsetRef.current = 0
    setHasMoreMessages(false)
    setLoadingOlder(false)
  }

  const handleSend = (e) => {
    e.preventDefault()
    if (!input.trim() || !socketRef.current?.connected || closed) return

    const content = input.trim()
    setInput('')
    setMessages(prev => [...prev, {
      _temp: true,
      senderRole: 'visitor',
      senderName: name,
      content,
      createdAt: new Date().toISOString(),
    }])
    scrollToBottom()

    socketRef.current.emit('message:send', { content })

    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
    socketRef.current.emit('typing:stop')
  }

  const handleInputChange = (e) => {
    setInput(e.target.value)
    if (socketRef.current?.connected) {
      socketRef.current.emit('typing:start')
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
      typingTimeoutRef.current = setTimeout(() => {
        socketRef.current?.emit('typing:stop')
      }, 1500)
    }
  }

  const formatTime = (iso) => {
    try {
      return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    } catch { return '' }
  }

  return (
    <div className={`chat-widget ${open ? 'open' : ''}`} style={{ bottom: cookieVisible ? 'calc(20px + 72px)' : '20px' }}>
      {open && (
        <div className="chat-widget-window">
            <div className="chat-widget-header">
              <span>{t('chat.title')}</span>
              <div className="chat-widget-status">
                <span className={`chat-status-dot ${(withinHours && adminOnline) ? 'online' : 'offline'}`} />
                {(withinHours && adminOnline) ? t('chat.online') : t('chat.offline')}
              </div>
              {conversationId && (
                <div className="chat-widget-chatid">{t('chat.chatId')}: {conversationId}</div>
              )}
              <button className="chat-widget-close" onClick={() => setOpen(false)}>×</button>
          </div>

          {showForm ? (
            <form className="chat-widget-form" onSubmit={handleStartChat}>
              <p>{t('chat.formPrompt')}</p>
              <input
                type="text"
                placeholder={t('chat.namePlaceholder')}
                value={name}
                onChange={e => setName(e.target.value)}
                required
                autoFocus
              />
              <input
                type="email"
                placeholder={t('chat.emailPlaceholder')}
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                pattern="[^\s@]+@[^\s@]+\.[^\s@]+"
              />
              <button type="submit" className="btn btn-primary" disabled={!name.trim() || !isValidEmail(email.trim())}>
                {t('chat.startChat')}
              </button>
            </form>
          ) : (
            <div className="chat-widget-body">
              <div className="chat-messages" ref={messagesRef} onScroll={handleScroll}>
                {hasMoreMessages && (
                  <button
                    type="button"
                    className="chat-load-older"
                    onClick={() => {
                      if (!loadingOlder) {
                        setLoadingOlder(true)
                        socketRef.current?.emit('visitor:load-older', { skip: olderOffsetRef.current, limit: 50 })
                      }
                    }}
                    disabled={loadingOlder}
                  >
                    {loadingOlder ? t('chat.loadingOlder') : t('chat.scrollOlder')}
                  </button>
                )}
                {messages.length === 0 && (
                  <div className="chat-empty">{t('chat.emptyMessages')}</div>
                )}
                {messages.map((msg, i) => (
                  <div key={msg.objectId || i} className={`chat-msg ${msg.senderRole === 'visitor' ? 'own' : 'other'}`}>
                    <div className="chat-msg-bubble">
                      <div className="chat-msg-text">{msg.content}</div>
                      <div className="chat-msg-time">{formatTime(msg.createdAt)}</div>
                    </div>
                  </div>
                ))}
                {adminTyping && (
                  <div className="chat-msg other">
                    <div className="chat-msg-bubble typing">
                      <span>.</span><span>.</span><span>.</span>
                    </div>
                  </div>
                )}
                {closed && (
                  <div className="chat-closed-banner">{t('chat.closed')}</div>
                )}
                <div ref={messagesEndRef} />
              </div>
              {closed ? (
                <button className="btn btn-primary chat-newchat-btn" onClick={handleNewChat}>
                  {t('chat.newChat')}
                </button>
              ) : (
                <form className="chat-input-bar" onSubmit={handleSend}>
                  <EmojiPicker onSelect={(emoji) => { setInput(prev => prev + emoji); inputRef.current?.focus() }} />
                  <input
                    ref={inputRef}
                    type="text"
                    placeholder={t('chat.inputPlaceholder')}
                    value={input}
                    onChange={handleInputChange}
                    disabled={!connected}
                    autoFocus
                  />
                  <button type="submit" disabled={!input.trim() || !connected}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="22" y1="2" x2="11" y2="13" />
                      <polygon points="22 2 15 22 11 13 2 9 22 2" />
                    </svg>
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      )}

      <button className="chat-widget-button" onClick={() => setOpen(!open)} aria-label={t('chat.toggleChat')}>
        {!open && (
          <span className={`chat-widget-button-dot ${(withinHours && adminOnline) ? 'online' : 'offline'}`} />
        )}
        {open ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        )}
      </button>
    </div>
  )
}
